import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageSquare, ShieldCheck } from "lucide-react";
import { COMPANY } from "../data";

const ease = [0.16, 1, 0.3, 1];

const EFFECTIVE_DATE = "January 1, 2025";

export function SmsTerms() {
  return (
    <div data-testid="sms-terms-page" className="page-enter pt-32 sm:pt-40">
      <section className="container-x pb-12 max-w-4xl">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">
          SMS Terms &amp; Conditions
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance text-[#0F1E3C] mb-6"
        >
          Clear terms for our messages.
        </motion.h1>
        <p className="text-base text-[#475569] leading-relaxed max-w-2xl">
          These SMS Terms &amp; Conditions govern your participation in the Albany Staffing
          (a DBA of Albany Systems LLC) text messaging program. By opting in, you agree to the
          following terms.
        </p>
      </section>

      <section className="container-x pb-24 max-w-4xl">
        <div className="card-soft rounded-2xl p-6 sm:p-8 mb-10 bg-white flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#FFF4EA] flex items-center justify-center text-[#E9610E] shrink-0">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.15em] text-[#E9610E] font-semibold mb-1">
              Reply <strong>STOP</strong> to opt out · Reply <strong>HELP</strong> for assistance
            </p>
            <p className="text-sm text-[#334155] leading-relaxed">
              Albany Staffing honors all opt-out requests immediately. SMS consent is never
              shared with third parties or affiliates for marketing purposes.
            </p>
          </div>
        </div>

        <div className="space-y-10 text-[#334155] text-base sm:text-lg leading-relaxed">
          <Section title="1. SMS Program Description" number="01">
            <p>Albany Staffing sends SMS messages regarding:</p>
            <ul className="list-disc list-outside ml-5 mt-3 space-y-1.5">
              <li>New job opportunities</li>
              <li>Candidate application updates</li>
              <li>Interview scheduling and reminders</li>
              <li>Recruiter communications</li>
              <li>Employment-related updates</li>
              <li>Client and business communications</li>
            </ul>
          </Section>

          <Section title="2. Opt-In Method" number="02">
            <p>You opt in to receive SMS messages from Albany Staffing by <strong className="text-[#0F1E3C]">both</strong>:</p>
            <ul className="list-disc list-outside ml-5 mt-3 space-y-1.5">
              <li>Submitting your phone number through an Albany Staffing website form (Contact Us,
                  Job Application, Candidate Registration, or Request Talent), <strong>and</strong></li>
              <li>Checking the clearly-labeled SMS consent checkbox on that form.</li>
            </ul>
            <p className="mt-3">
              You may also opt in by explicitly requesting SMS communication from a recruiter or
              representative in writing or through direct conversation.
            </p>
            <p className="mt-3">
              Every website form on this site that collects a phone number displays a required,
              unchecked SMS consent checkbox that links to this SMS Terms page and to our Privacy
              Policy. Submitting a phone number without checking that box does <em>not</em> opt you in
              to SMS messages.
            </p>
          </Section>

          <Section title="3. Message Frequency" number="03">
            <p>
              Message frequency varies depending on your application status, available job
              opportunities, and communication needs. You will not receive automated marketing
              blasts — messages are sent only when relevant to your engagement with us.
            </p>
          </Section>

          <Section title="4. Message & Data Rates" number="04">
            <p>
              Message and data rates may apply based on your mobile carrier&rsquo;s plan. Albany
              Staffing LLC does not charge for SMS messages; however, standard carrier fees may apply.
            </p>
          </Section>

          <Section title="5. Opt-Out" number="05">
            <p>
              You can opt out of SMS communications at any time by replying{" "}
              <strong className="text-[#0F1E3C]">STOP</strong> to any message you receive from
              Albany Staffing. After you opt out, you will receive a single confirmation
              message and will not receive further SMS communications unless you opt in again.
            </p>
          </Section>

          <Section title="6. Help" number="06">
            <p>
              For help or questions about the SMS program, reply{" "}
              <strong className="text-[#0F1E3C]">HELP</strong> to any message or contact Albany
              Staffing LLC directly at{" "}
              <a href={`mailto:${COMPANY.email}`} className="text-[#E9610E] underline">
                {COMPANY.email}
              </a>
              .
            </p>
          </Section>

          <Section title="7. Privacy Policy Reference" number="07">
            <p>
              Use of the SMS service is subject to Albany Staffing&rsquo;s{" "}
              <Link to="/privacy" className="text-[#E9610E] underline hover:text-[#0F1E3C]">
                Privacy Policy
              </Link>
              , which describes how we collect, use, and safeguard your information.
            </p>
          </Section>

          <Section title="8. Required Statement" number="08">
            <div className="card-soft rounded-xl p-5 sm:p-6 bg-[#FFF4EA] border-[#F8D5B0]">
              <p className="text-[#0F1E3C] font-semibold flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-[#E9610E] mt-0.5 shrink-0" />
                SMS consent is not shared with third parties or affiliates for marketing purposes.
              </p>
            </div>
          </Section>

          <Section title="9. Contact" number="09">
            <div className="card-soft rounded-2xl p-6 bg-white not-prose">
              <p className="text-[#0F1E3C] font-display font-bold text-lg">Albany Staffing</p>
              <p className="text-sm text-[#64748B] mt-0.5">A DBA of Albany Systems LLC</p>
              <p className="mt-3 text-sm"><span className="text-[#64748B]">Address:</span> {COMPANY.address}</p>
              <p className="mt-1 text-sm">
                <span className="text-[#64748B]">Email:</span>{" "}
                <a href={`mailto:${COMPANY.email}`} className="text-[#E9610E] hover:text-[#0F1E3C]">{COMPANY.email}</a>
              </p>
              <p className="mt-1 text-sm"><span className="text-[#64748B]">Phone:</span> {COMPANY.phone}</p>
            </div>
          </Section>

          <Section title="10. Effective Date" number="10">
            <p>
              These SMS Terms &amp; Conditions are effective as of{" "}
              <strong className="text-[#0F1E3C]">{EFFECTIVE_DATE}</strong>. Albany Staffing may
              update or revise these terms at any time. Material changes will be posted on this
              page with a revised effective date.
            </p>
          </Section>
        </div>

        <p className="text-xs text-[#64748B] pt-12 mt-4 border-t border-[#EAE3D2]">
          Last updated: {EFFECTIVE_DATE}
        </p>
      </section>
    </div>
  );
}

function Section({ title, number, children }) {
  return (
    <div data-testid={`sms-section-${number}`}>
      <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#0F1E3C] mb-4">
        {title.replace(/^\d+\.\s*/, "")}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
